import { Link } from "react-router";
import { Clock3, ExternalLink, ShieldAlert } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { PublicEchoFeedbackRecord } from "@/server/echoFeedback";
import type { EchoflicksSessionFramingEvent } from "../types";
import { formatVideoTimestamp } from "../echoflicks/sessionUtils";
import { AuthenticatedFeedbackImage } from "./AuthenticatedFeedbackImage";
import { effectiveFeedbackCorrelation } from "./framingFeedback";

type FramingFeedbackEvidenceProps = {
  reports: PublicEchoFeedbackRecord[];
  framingEvents?: EchoflicksSessionFramingEvent[];
};

/** Exact reviewer evidence, including legacy reports without v2 crop telemetry. */
export function FramingFeedbackEvidence({
  reports,
  framingEvents,
}: FramingFeedbackEvidenceProps) {
  if (!reports.length) return null;
  return (
    <section className="space-y-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
      <div>
        <h3 className="flex items-center gap-2 font-medium">
          <ShieldAlert className="size-4 text-destructive" /> Reviewer crop
          evidence
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Exact private frame and delivered-video timestamp. Legacy reports are
          preserved without inventing applied crop geometry.
        </p>
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        {reports.map((report) => {
          const correlation = effectiveFeedbackCorrelation(
            report,
            framingEvents,
          );
          const feedbackDetailSearch = new URLSearchParams({
            echoId: report.echoId,
            feedbackId: report.id,
          });
          return (
            <article
              key={report.id}
              id={`feedback-${report.id}`}
              className="overflow-hidden rounded-md border bg-background"
            >
              {report.hasFrame ? (
                <div className="h-44 bg-black">
                  <AuthenticatedFeedbackImage
                    reportId={report.id}
                    alt={`Private feedback frame at ${formatVideoTimestamp(report.timestampSeconds)}`}
                  />
                </div>
              ) : null}
              <div className="space-y-2 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="destructive">
                    {report.categories
                      .map((category) => category.label)
                      .join(", ") || "Framing & crop"}
                  </Badge>
                  <Badge variant="outline" className="font-mono">
                    <Clock3 className="size-3" />
                    {formatVideoTimestamp(report.timestampSeconds)}
                  </Badge>
                  {correlation.sceneId ? (
                    <Badge variant="secondary">
                      scene {correlation.sceneId} / attempt{" "}
                      {correlation.attemptId ?? "legacy"}
                    </Badge>
                  ) : (
                    <Badge variant="secondary">
                      legacy / applied crop evidence unavailable
                    </Badge>
                  )}
                </div>
                <p className="text-sm">{report.feedback}</p>
                <div className="flex flex-wrap gap-2">
                  <Button asChild variant="outline" size="sm">
                    <a href="#echo-final-video">Final Echo</a>
                  </Button>
                  <Button asChild variant="outline" size="sm">
                    <a href="#echo-native-sources">Native sources</a>
                  </Button>
                  <Button asChild variant="outline" size="sm">
                    <a href="#echo-storyboard">Storyboard</a>
                  </Button>
                  <Button asChild variant="outline" size="sm">
                    <Link to={`/echo_feedback?${feedbackDetailSearch}`}>
                      Feedback detail <ExternalLink className="size-3" />
                    </Link>
                  </Button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
